import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import React from "react";
import "./FadeIn.css";

const useStyles = makeStyles((theme) => ({
  skillContainer: {
    backgroundColor: "#141414",
    width: "100%",
    paddingBottom: "100px",
    paddingTop: "100px",
    position: "relative",
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontWeight: 700,
    marginBottom: "16px",
    position: "relative",
    display: "inline-block",
  },
  sectionSubtitle: {
    color: "#707070",
    fontSize: "1rem",
    marginBottom: "60px",
    maxWidth: "500px",
  },
  greenAccent: {
    color: "#00FF41",
  },
  titleUnderline: {
    width: "80px",
    height: "3px",
    background: "linear-gradient(90deg, #00FF41 0%, transparent 100%)",
    marginBottom: "16px",
    borderRadius: "2px",
  },
  card: {
    background: "rgba(20, 20, 20, 0.8)",
    backdropFilter: "blur(10px)",
    border: "1px solid #2A2A2A",
    borderRadius: "16px",
    height: "100%",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background: "linear-gradient(90deg, #00FF41 0%, transparent 100%)",
      opacity: 0,
      transition: "opacity 0.3s ease",
    },
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 20px 40px rgba(0, 255, 65, 0.15)",
      borderColor: "#00FF41",
      "&::before": {
        opacity: 1,
      },
    },
  },
  cardContent: {
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    height: "100%",
  },
  imageContainer: {
    width: "100px",
    height: "100px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    borderRadius: "12px",
    background: "#FFFFFF",
    padding: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: "-2px",
      left: "-2px",
      right: "-2px",
      bottom: "-2px",
      borderRadius: "14px",
      background:
        "linear-gradient(135deg, rgba(0, 255, 65, 0.3) 0%, transparent 50%, transparent 100%)",
      zIndex: -1,
    },
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  cardTitle: {
    color: "#FFFFFF",
    fontWeight: 600,
    fontSize: "1.1rem",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  cardNumber: {
    color: "#00FF41",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: "0.7rem",
    fontWeight: 500,
    opacity: 0.7,
  },
  cardDescription: {
    color: "#B0B0B0",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  skillIndicator: {
    display: "flex",
    gap: "4px",
    marginTop: "16px",
  },
  skillDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#00FF41",
    opacity: 0.3,
    "&.active": {
      opacity: 1,
      boxShadow: "0 0 8px rgba(0, 255, 65, 0.5)",
    },
  },
}));

const skills = [
  {
    title: "Frontend",
    image: "/images/skillPics/frontend.png",
    description:
      "I have professional experience using HTML, CSS, JS, and React to create responsive websites. I also understand state management systems like Redux.",
    level: 5,
  },
  {
    title: "AWS Developer",
    image: "/images/skillPics/aws.png",
    description:
      "I've worked extensively with AWS Lambda, DynamoDB, S3, API Gateway, AWS Bedrock, serverless architectures, CI/CD pipelines, and cloud infrastructure development.",
    level: 5,
  },
  {
    title: "Backend",
    image: "/images/skillPics/node.jpg",
    description:
      "I build and maintain Backends using a variety of technologies such as Node.JS, Express, Python, and Flask. My work using these technologies can be seen in the Projects section.",
    level: 5,
  },
  {
    title: "DB Architecture",
    image: "/images/skillPics/sql_mongodb.png",
    description:
      "I am well versed in SQL and MongoDB databases and have professional experience using technologies that interact with these databases such as Mongoose, Node-PG, SQL Alchemy, and Sequelize.",
    level: 5,
  },
  {
    title: "REST & SOAP APIs",
    image: "/images/skillPics/api.jpg",
    description:
      "I create API routes and models allowing users to create, retrieve, update, and delete stored data. I also develop code to communicate with external APIs through various technologies.",
    level: 5,
  },
  {
    title: "Frontend Design",
    image: "/images/skillPics/antdesign.png",
    description:
      "The look and feel of applications are important in every project. I understand CSS and many CSS frameworks such as Ant Design, Material UI, and Bootstrap.",
    level: 5,
  },
];

export default function Skills() {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={classes.skillContainer}>
      <span id="Skills" style={{ position: "absolute", marginTop: "-80px" }}></span>
      <Container maxWidth="md">
        <div className={classes.titleUnderline} />
        <Typography variant="h4" className={classes.sectionTitle}>
          My <span className={classes.greenAccent}>Skills</span>
        </Typography>
        <Typography className={classes.sectionSubtitle}>
          Technologies and tools I use to bring ideas to life
        </Typography>
        <Grid container spacing={4}>
          {skills.map((skill, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card className={classes.card} elevation={0}>
                <CardContent className={classes.cardContent}>
                  <div className={classes.imageContainer}>
                    <img
                      className={classes.image}
                      src={skill.image}
                      alt={skill.title}
                    />
                  </div>
                  <Typography className={classes.cardTitle}>
                    <span className={classes.cardNumber}>0{index + 1}</span>
                    {skill.title}
                  </Typography>
                  <Typography className={classes.cardDescription}>
                    {skill.description}
                  </Typography>
                  <div className={classes.skillIndicator}>
                    {[1, 2, 3, 4, 5].map((dot) => (
                      <div
                        key={dot}
                        className={`${classes.skillDot} ${
                          dot <= skill.level ? "active" : ""
                        }`}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Grid>
  );
}
